/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { BrowserCacheLocation } from '@azure/msal-browser';
import { z } from 'zod';

const tokenIndexSchema = z.object({ idToken: z.array(z.string()) });
const emptyIdTokenSchema = z.object({
  clientId: z.string(),
  credentialType: z.literal('IdToken'),
  secret: z.literal('').nullish(),
});

export const prepareMsalCache = (cacheLocation: BrowserCacheLocation, clientId: string): void => {
  if (cacheLocation === 'memoryStorage') return;
  try {
    const storage = cacheLocation === 'localStorage' ? window.localStorage : window.sessionStorage;
    const removedCount = removeEmptyCachedIdTokens(storage, clientId);
    if (removedCount > 0) {
      // biome-ignore lint/suspicious/noConsole: count-only diagnostics for authentication recovery
      console.warn(`Removed ${removedCount} empty MSAL ID token cache entries`);
    }
  } catch {
    // Best-effort: let MSAL choose its storage fallback when browser storage is blocked.
    // biome-ignore lint/suspicious/noConsole: no cache contents or exception details are logged
    console.warn('Skipped MSAL cache cleanup: browser storage unavailable');
  }
};

// MSAL 4.30 (and 5.25.1) decodes every indexed ID token in getKMSIValues during
// initialization. An empty secret aborts startup before login can recover it.
// Remove only unusable ID tokens belonging to this app; leave other cache data intact.
export const removeEmptyCachedIdTokens = (storage: Storage, clientId: string): number => {
  let removedCount = 0;
  for (const indexKey of Object.keys(storage)) {
    if (/^msal(?:\.\d+)?\.token\.keys\.(.+)$/.exec(indexKey)?.[1] !== clientId) continue;
    const index = tokenIndexSchema.safeParse(parseCacheEntry(storage.getItem(indexKey)));
    if (!index.success) continue;

    for (const tokenKey of index.data.idToken) {
      const token = emptyIdTokenSchema.safeParse(parseCacheEntry(storage.getItem(tokenKey)));
      if (token.success && token.data.clientId === clientId) {
        storage.removeItem(tokenKey);
        removedCount++;
      }
    }
  }
  return removedCount;
};

const parseCacheEntry = (value: string | null): unknown => {
  try {
    return value ? JSON.parse(value) : undefined;
  } catch {
    return undefined;
  }
};
